import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { RefreshCw, ArrowLeft, ArrowRight, RotateCcw, Bell, Mail, Circle, Plus, Link2, GitBranch, Move, Send, Users, Calculator, X, ChevronDown } from 'lucide-react'
import { usePipelineContext } from '../PipelineContext'
import axios from '../../../../config/axios'
import styled from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: transparent;
`

const SubHeaderContainer = styled.div`
  background-color: #fff;
  padding: 1.3rem 4.1rem;
  display: flex;
  justify-content: space-between;
  border-radius: .8rem;
  position: relative;
  z-index: 1;
`

const LeftSide = styled.div`
  display: flex;
  align-items: center;
`

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SectionName = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
`

const CancelButton = styled.div`
  font-weight: bold;
  user-select: none;
  display: flex;
  height: 100%;
  align-items: center;
  font-size: .8rem;
  padding: 0.5rem 1.5rem;
  color: #0090D3;
  cursor: pointer;
  background-color: transparent;
  border: 2px solid #0090D3;
  border-radius: 2rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #E6F4FB;
  }
`

const CreateButton = styled.div`
  font-weight: bold;
  user-select: none;
  display: flex;
  height: 100%;
  align-items: center;
  font-size: .8rem;
  padding: 0.5rem 1.5rem;
  color: #fff;
  cursor: pointer;
  background-color: #0090D3;
  border-radius: 2rem;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #007bb5;
  }
`

const BreadcrumbsContainer = styled.nav`
  font-size: 14px;
  padding: 1rem 0;
  background-color: transparent;
  
  ol {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
  }
  
  li {
    display: flex;
    align-items: center;
    color: #666;
    
    &:not(:last-child)::after {
      content: '>';
      margin: 0 8px;
      color: #999;
    }
    
    a {
      text-decoration: none;
      color: #0090d3;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
    
    span {
      color: #333;
    }
  }
`

const Content = styled.div`
  flex: 1;
  padding: 0;
  overflow: auto;
  background-color: transparent;
`

const FormCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 2rem;
`

const FormGroup = styled.div<{ $flex?: number }>`
  display: flex;
  flex-direction: column;
  flex: ${props => props.$flex || 1};

  label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  input {
    padding: 12px 16px;
    border: none;
    border-radius: 100px;
    font-size: 14px;
    background-color: #EBEBEB;
    color: #333;

    &::placeholder {
      color: #999;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.2);
    }
  }
`

const SelectionContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  padding: 2rem 0;
`

const SelectionCard = styled.div`
  width: 320px;
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
`

const EventConfigCard = styled.div`
  width: 320px;
  background-color: #fff;
  border-radius: 8px;
  padding: 1.5rem;
`

const EventConfigHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`

const EventConfigTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #333;

  svg {
    color: #696969;
    width: 18px;
    height: 18px;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #696969;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`

const SelectLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: block;
`

const SelectWrapper = styled.div`
  position: relative;
`

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: none;
  border-radius: 100px;
  font-size: 14px;
  background-color: #EBEBEB;
  color: #333;
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 144, 211, 0.2);
  }
`

const SelectIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #696969;
`

const SelectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 1rem 0;
`

const SelectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const SelectionItem = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background-color: ${props => props.$selected ? '#E6F4FB' : '#F5F5F5'};
  border: 1px solid ${props => props.$selected ? '#0090D3' : 'transparent'};
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.$disabled ? '#F5F5F5' : props.$selected ? '#E6F4FB' : '#EBEBEB'};
  }

  svg {
    color: ${props => props.$disabled ? '#BEBEBE' : '#696969'};
    width: 18px;
    height: 18px;
  }

  span {
    font-size: 14px;
    color: ${props => props.$disabled ? '#BEBEBE' : '#333'};
  }
`

const events = [
  { id: 'card_created', label: 'Card é criado', icon: RefreshCw },
  { id: 'card_enter_phase', label: 'Card entra na fase', icon: ArrowLeft },
  { id: 'card_exit_phase', label: 'Card sai da fase', icon: ArrowRight },
  { id: 'field_updated', label: 'Campo é atualizado', icon: RotateCcw },
  { id: 'recurring_activity', label: 'Atividade recorrente', icon: RefreshCw },
  { id: 'alert_triggered', label: 'Alerta acionado', icon: Bell },
  { id: 'email_received', label: 'E-mail recebido', icon: Mail },
  { id: 'extra_event', label: 'Evento extra', icon: Circle },
]

const actions = [
  { id: 'create_card', label: 'Criar um card', icon: Plus },
  { id: 'create_connected_card', label: 'Criar um card conectado', icon: Link2 },
  { id: 'move_parent_card', label: 'Mover um card pai', icon: GitBranch },
  { id: 'update_field', label: 'Atualizar um campo', icon: RotateCcw },
  { id: 'move_card', label: 'Mover o card', icon: Move },
  { id: 'send_email', label: 'Enviar um e-mail', icon: Send },
  { id: 'distribute_responsible', label: 'Distribua responsáveis', icon: Users },
  { id: 'create_formula', label: 'Crie uma fórmula', icon: Calculator },
]

const eventsWithStepConfig = ['card_created', 'card_enter_phase', 'card_exit_phase']

interface Step {
  id: number
  name: string
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string; onClick?: () => void }[] }> = ({
  items,
}) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : item.onClick ? (
            <a onClick={item.onClick}>{item.label}</a>
          ) : (
            <span style={{color: '#0090d3', cursor: 'pointer'}}>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const CreateAutomation: React.FC = () => {
  const [searchParams] = useSearchParams()
  const pipelineId = searchParams.get('pipelineId')
  const { pipelineName, setIsCreatingAutomation } = usePipelineContext()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null)
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const [selectedStep, setSelectedStep] = useState<string>('')
  const [steps, setSteps] = useState<Step[]>([])

  useEffect(() => {
    const fetchSteps = async () => {
      if (!pipelineId) return
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${pipelineId}`
        )
        if (response.data?.data) {
          setSteps(response.data.data)
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchSteps()
  }, [pipelineId])

  const breadcrumbItems = [
    { label: 'Funis', path: '/panel/pipelines' },
    { label: pipelineName || 'Nome do funil' },
    { label: 'Automação', onClick: () => setIsCreatingAutomation(false) },
    { label: 'Nova automação' },
  ]

  const handleCancel = () => {
    setIsCreatingAutomation(false)
  }

  const handleCreate = () => {
    console.log({ name, description, selectedEvent, selectedAction, selectedStep })
  }

  const handleEventSelect = (eventId: string) => {
    setSelectedEvent(eventId)
    if (!eventsWithStepConfig.includes(eventId)) {
      setSelectedStep('')
    }
  }

  const handleCloseEventConfig = () => {
    setSelectedEvent(null)
    setSelectedStep('')
  }

  const getSelectedEventData = () => {
    return events.find(e => e.id === selectedEvent)
  }

  const showEventConfig = selectedEvent && eventsWithStepConfig.includes(selectedEvent)

  return (
    <Container>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>Nova automação</SectionName>
        </LeftSide>
        <RightSide>
          <CancelButton onClick={handleCancel}>
            Cancelar
          </CancelButton>
          <CreateButton onClick={handleCreate}>
            <Plus size={16} /> Criar automação
          </CreateButton>
        </RightSide>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <Content>
        <FormCard>
          <FormGroup $flex={1}>
            <label>Nome da automação</label>
            <input 
              type="text" 
              placeholder="Digite aqui"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormGroup>
          <FormGroup $flex={2.5}>
            <label>Descrição</label>
            <input 
              type="text" 
              placeholder="Selecione"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormGroup>
        </FormCard>

        <SelectionContainer>
          {showEventConfig ? (
            <EventConfigCard>
              <EventConfigHeader>
                <EventConfigTitle>
                  {getSelectedEventData() && (
                    <>
                      {React.createElement(getSelectedEventData()!.icon, { size: 18 })}
                      Quando {getSelectedEventData()!.label.toLowerCase()}
                    </>
                  )}
                </EventConfigTitle>
                <CloseButton onClick={handleCloseEventConfig}>
                  <X size={18} />
                </CloseButton>
              </EventConfigHeader>
              <SelectLabel>Etapa</SelectLabel>
              <SelectWrapper>
                <StyledSelect 
                  value={selectedStep} 
                  onChange={(e) => setSelectedStep(e.target.value)}
                >
                  <option value="">Selecione uma etapa</option>
                  {steps.map((step) => (
                    <option key={step.id} value={step.id}>{step.name}</option>
                  ))}
                </StyledSelect>
                <SelectIcon>
                  <ChevronDown size={18} />
                </SelectIcon>
              </SelectWrapper>
            </EventConfigCard>
          ) : (
            <SelectionCard>
              <SelectionTitle>Selecione o evento</SelectionTitle>
              <SelectionList>
                {events.map((event) => (
                  <SelectionItem 
                    key={event.id}
                    $selected={selectedEvent === event.id}
                    onClick={() => handleEventSelect(event.id)}
                  >
                    <event.icon />
                    <span>{event.label}</span>
                  </SelectionItem>
                ))}
              </SelectionList>
            </SelectionCard>
          )}

          <SelectionCard>
            <SelectionTitle>Selecione a ação</SelectionTitle>
            <SelectionList>
              {actions.map((action) => (
                <SelectionItem 
                  key={action.id}
                  $selected={selectedAction === action.id}
                  $disabled={!selectedEvent}
                  onClick={() => selectedEvent && setSelectedAction(action.id)}
                >
                  <action.icon />
                  <span>{action.label}</span>
                </SelectionItem>
              ))}
            </SelectionList>
          </SelectionCard>
        </SelectionContainer>
      </Content>
    </Container>
  )
}

export default CreateAutomation
